if (!customElements.get('chase-size-picker')) {
  customElements.define('chase-size-picker', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.list = this.querySelector('[data-chase-size-list]');
      this.status = this.querySelector('[data-chase-size-status]');
      this.error = this.querySelector('[data-chase-size-error]');
      this.details = this.querySelector('[data-chase-size-details]');
      this.detailsLabel = this.details.textContent;
      this.closeButton = this.querySelector('[data-chase-size-close]');
      this.mobile = window.matchMedia('(max-width: 989px)');
      this.onTrigger = (event) => {
        const trigger = event.target.closest('[data-chase-quick-add]');
        if (!trigger || !this.mobile.matches || this.busy) return;
        event.preventDefault();
        this.open(trigger);
      };
      this.onResize = () => { if (!this.mobile.matches) this.close(true); };
      document.addEventListener('click', this.onTrigger);
      this.mobile.addEventListener('change', this.onResize);
      this.closeButton.addEventListener('click', () => this.close());
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); });
      this.dialog.addEventListener('close', () => document.documentElement.classList.remove('chase-size-picker-open'));
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientY < rect.top || event.clientY > rect.bottom || event.clientX < rect.left || event.clientX > rect.right) this.close();
      });
      this.list.addEventListener('click', (event) => {
        const button = event.target.closest('[data-chase-variant]');
        if (button && !button.disabled && !this.busy) this.add(button);
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onTrigger);
      this.mobile?.removeEventListener('change', this.onResize);
      this.controller?.abort();
      document.documentElement.classList.remove('chase-size-picker-open');
      this.initialized = false;
    }

    async open(trigger) {
      const productUrl = new URL(trigger.dataset.productUrl, location.origin);
      if (productUrl.origin !== location.origin) return;
      if (typeof this.dialog.showModal !== 'function') { location.assign(productUrl.href); return; }
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.trigger = trigger;
      this.list.replaceChildren();
      this.error.hidden = true;
      this.error.textContent = '';
      this.status.textContent = this.dataset.loading;
      this.details.href = productUrl.href;
      this.details.textContent = this.detailsLabel;
      this.details.hidden = true;
      this.querySelector('#ChaseSizePickerProduct').textContent = trigger.dataset.productTitle;
      let product;
      try { product = JSON.parse(trigger.dataset.productSizes || 'null'); } catch {}
      if (product && Array.isArray(product.variants)) this.renderProduct(product);
      this.dialog.showModal();
      this.dialog.scrollTop = 0;
      document.documentElement.classList.add('chase-size-picker-open');
      if (product && Array.isArray(product.variants)) return;
      productUrl.pathname = productUrl.pathname.replace(/\/$/, '') + '.js';
      productUrl.search = '';
      try {
        const response = await fetch(productUrl.href, { signal: controller.signal, credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error(this.dataset.loadError);
        const product = await response.json();
        if (controller.signal.aborted || !this.dialog.open) return;
        this.renderProduct(product);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.status.textContent = '';
        this.error.textContent = this.dataset.loadError;
        this.error.hidden = false;
        this.details.hidden = false;
      }
    }

    renderProduct(product) {
      this.status.textContent = '';
      if (product.details_required || product.requires_selling_plan || !Array.isArray(product.variants) || !product.variants.length) {
        this.status.textContent = this.dataset.detailsRequired;
        this.details.hidden = false;
        return;
      }
      const rows = document.createDocumentFragment();
      for (const variant of product.variants) {
        const item = document.createElement('li');
        const button = document.createElement('button');
        const label = variant.title === 'Default Title' ? this.dataset.oneSize : variant.title;
        button.type = 'button';
        button.className = 'chase-size-picker__size';
        button.dataset.chaseVariant = String(variant.id);
        button.dataset.quantity = String(Math.max(1, variant.quantity_rule?.min || 1));
        const selectable = variant.available || variant.store_purchase === true;
        button.dataset.available = String(selectable);
        button.textContent = label;
        if (!selectable) {
          button.disabled = true;
          button.classList.add('is-sold-out');
          button.setAttribute('aria-label', label + ' — ' + this.dataset.soldOut);
        }
        item.append(button);
        rows.append(item);
      }
      this.list.replaceChildren(rows);
    }

    close(force = false) {
      if (this.busy && !force) return;
      this.controller?.abort();
      this.dialog.close();
      document.documentElement.classList.remove('chase-size-picker-open');
    }

    async add(button) {
      this.storePurchaseSelected = false;
      this.details.hidden = true;
      this.details.textContent = this.detailsLabel;
      this.busy = true;
      this.closeButton.disabled = true;
      this.error.hidden = true;
      this.status.textContent = this.dataset.storePurchaseEnabled === 'true' ? this.dataset.checkingStores : this.dataset.adding;
      this.list.setAttribute('aria-busy', 'true');
      this.list.querySelectorAll('button').forEach((item) => { item.disabled = true; });
      const root = window.Shopify?.routes?.root || '/';
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const sections = cart?.getSectionsToRender?.().map((section) => section.id) || [];
      const body = { id: Number(button.dataset.chaseVariant), quantity: Number(button.dataset.quantity) };
      if (sections.length) {
        body.sections = sections;
        body.sections_url = location.pathname + location.search;
        cart.setActiveElement?.(this.trigger);
      }
      try {
        if (this.dataset.storePurchaseEnabled === 'true') {
          const availability = await fetch(root + 'variants/' + body.id + '/?section_id=pickup-availability', { credentials:'same-origin', cache:'no-store', signal:AbortSignal.timeout(10000) });
          if (!availability.ok) throw new Error(this.dataset.loadError);
          const html = new DOMParser().parseFromString(await availability.text(),'text/html');
          const notice = html.querySelector('template[data-chase-cart-fulfillment]')?.content.querySelector('chase-fulfillment-notice');
          if (!notice) throw new Error(this.dataset.loadError);
          if (notice.dataset.storePurchase === 'true') {
            this.storePurchaseSelected = true;
            this.status.textContent = this.dataset.storePurchaseMessage;
            const productUrl = new URL(this.trigger.dataset.productUrl,location.origin);
            productUrl.searchParams.set('variant',String(body.id));
            productUrl.hash = 'chase-store-contact-' + body.id;
            this.details.href = productUrl.href;
            this.details.textContent = this.dataset.storePurchaseDetails;
            this.details.hidden = false;
            return;
          }
          this.status.textContent = this.dataset.adding;
        }
        const response = await fetch(window.routes?.cart_add_url || root + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(body)
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(typeof result.description === 'string' ? result.description : this.dataset.addError);
        }
        this.close(true);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'chase-mobile-quick-add', productVariantId: body.id, cartData: result });
        }
        if (!cart || !sections.every((id) => typeof result.sections?.[id] === 'string')) {
          location.assign(window.routes?.cart_url || root + 'cart');
          return;
        }
        try {
          cart.classList.remove('is-empty');
          cart.renderContents(result);
        } catch (error) {
          location.assign(window.routes?.cart_url || root + 'cart');
        }
      } catch (error) {
        this.error.textContent = error.name === 'TimeoutError' || error.message === 'Failed to fetch' ? this.dataset.loadError : error.message || this.dataset.addError;
        this.details.hidden = false;
        this.error.hidden = false;
      } finally {
        this.busy = false;
        this.closeButton.disabled = false;
        if (!this.storePurchaseSelected) this.status.textContent = '';
        this.list.removeAttribute('aria-busy');
        this.list.querySelectorAll('button').forEach((item) => { item.disabled = item.dataset.available !== 'true'; });
      }
    }
  });
}

